import apiClient from './apiClient'; // Import the centralized apiClient
import type { Character } from '../store/slices/charactersSlice';
import { getItemCounts } from './itemService';

export const getCharacters = async (game?: string, league?: string): Promise<Character[]> => {
  const response = await apiClient.get<Character[]>('characters', {
    params: {
      game,
      league,
    },
  });

  // Fetch Divine Orb counts for the characters
  try {
    const divineOrbCounts = await getItemCounts({
      itemType: 'Divine Orb',
      game: game || 'PoE1',
      league: league || 'Standard',
    });

    // Map Divine Orb counts to characters
    const charactersWithDivineOrbCounts = response.data.map(character => {
      const divineOrbData = divineOrbCounts.find(item => item.character_id === character.id);

      return {
        ...character,
        divineOrbCount: divineOrbData ? divineOrbData.count : 0,
        // Keep the original gameAccount data from backend as it already has proper relations loaded
        gameAccount: character.gameAccount || { id: '', name: 'Unknown Account' },
      };
    });

    return charactersWithDivineOrbCounts;
  } catch (countError) {
    console.error('Failed to fetch Divine Orb counts:', countError);
    // Return characters without Divine Orb counts if the fetch fails
    return response.data;
  }
};

export const getBulkCharacterDeaths = async (characterIds: string[], hours: number): Promise<{ [key: string]: number }> => {
  try {
    const response = await apiClient.post<{ [key: string]: number }>('/characters/deaths/count', { character_ids: characterIds, hours });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch bulk character deaths:', error);
    throw error;
  }
};

export const getBulkCharacterVoices = async (characterIds: string[], game: string, league: string): Promise<{ [key: string]: number }> => {
  try {
    const results = await getItemCounts({
      itemName: 'Voices',
      game,
      league,
      characterIds,
    });

    // Convert array format to record format
    const record: { [key: string]: number } = {};
    characterIds.forEach(id => {
      record[id] = 0; // Initialize all to 0
    });

    results.forEach(result => {
      record[result.character_id] = result.count;
    });

    return record;
  } catch (error: any) {
    console.error('Failed to fetch bulk character voices:', error);
    throw error;
  }
};
// why not get?
export const getBulkIsAlive = async (characterIds: string[]): Promise<{ [key: string]: string | null }> => {
  try {
    const response = await apiClient.post<{ [key: string]: string | null }>('/characters/alive', { character_ids: characterIds });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch bulk character alive statuses:', error);
    throw error;
  }
};

export const getBulkLastInventoryUpdate = async (characterIds: string[]): Promise<{ [key: string]: string | null }> => {
  try {
    const response = await apiClient.post<{ [key: string]: string | null }>('/characters/last-inventory-update', {
      character_ids: characterIds,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch bulk character last inventory update timestamps:', error);
    throw error;
  }
};

export const updateCharacterNote = async (characterId: string, note: string): Promise<Character> => {
  try {
    const response = await apiClient.patch<Character>(`/characters/${characterId}/note`, { note });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to update character note:`, error);
    throw error;
  }
};

export const updateCharacterBoostingState = async (characterId: string, boostingState: number): Promise<Character> => {
  try {
    const response = await apiClient.patch<Character>(`/characters/${characterId}/boosting-state`, { boostingState });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to update character boosting state:`, error);
    throw error;
  }
};

export const deleteCharacter = async (characterId: string): Promise<void> => {
  try {
    await apiClient.delete(`/characters/${characterId}`);
  } catch (error: any) {
    console.error(`Failed to delete character:`, error);
    throw error;
  }
};

export const bulkDeleteCharacters = async (characterIds: string[]): Promise<{ deletedCount: number; errors: string[] }> => {
  try {
    const response = await apiClient.delete<{ deletedCount: number; errors: string[] }>('/characters/bulk', {
      data: { characterIds },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to bulk delete characters:`, error);
    throw error;
  }
};

export const bulkUpdateCharacterNote = async (
  characterIds: string[],
  note: string | null,
): Promise<{ updated: number }> => {
  try {
    const response = await apiClient.patch<{ updated: number }>('/characters/bulk-note', {
      characterIds,
      note,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to bulk update character notes:', error);
    throw error;
  }
};

// If this service grows, we might export the apiClient from here as well,
// but for now, slices can get it from authService.
// export default apiClient; // Not needed if slices import from authService directly
